import { inject, LOCALE_ID, Pipe, PipeTransform } from "@angular/core";

import round from "lodash-es/round";

import { IBaseUnit, ICustomUnitOfMeasurement } from "@shared/interfaces";
import { AuthenticationService } from "@shared/services";
import { CommonUtils } from "@shared/utils";

@Pipe({
  standalone: false,
  name: "unitConversion",
})
export class UnitConversionPipe implements PipeTransform {
  private readonly locale: string = inject(LOCALE_ID);

  private readonly authenticationService = inject(AuthenticationService);

  transform(
    value: string,
    customUnit: ICustomUnitOfMeasurement | IBaseUnit,
    systemUnit: IBaseUnit,
  ): string {
    // If the user is not allowed, or any param is missing, return an empty string
    if (
      !value ||
      isNaN(parseFloat(value)) ||
      !customUnit ||
      !systemUnit ||
      this.authenticationService.isSharedUser()
    ) {
      return "";
    }

    // Convert the incoming string to a number
    const numericValue = parseFloat(value);

    // 1) Determine the conversion factor (customUnit => systemUnit => divide)
    const factor = customUnit.conversionFactor ?? 1;

    // 2) Decide how many decimals to keep.
    const displayScale = systemUnit.precision ?? 0;

    // 3) Convert from custom to system => value * factor
    let convertedValue = numericValue * factor;

    // 4) Round to the desired decimal scale
    convertedValue = round(convertedValue, displayScale);

    // 5) Since 'toLocaleString' rounds the number again and we don't want that
    //    We use it's options to display the same number of digits that the
    //    current convertedValue has.
    const fractionDigits = CommonUtils.getNumberFractionDigits(convertedValue);

    // 5) Format with the user’s locale, then append the system unit symbol
    //    e.g. "5.81 m3"
    const formatted = convertedValue.toLocaleString(this.locale, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });

    return `${formatted} ${systemUnit.symbol ?? ""}`.trim();
  }
}
